import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { arBlockerad } from '../lib/authGuard'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [medarbetare, setMedarbetare] = useState(null)
  const [loading, setLoading] = useState(true)
  const senasteEfterfragadeId = useRef(null) // spärr mot "gamla" svar som hinner ikapp efter en session-återställning

  useEffect(() => {
    // Hämta aktiv session – sessionen sparas automatiskt i localStorage av Supabase
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        senasteEfterfragadeId.current = session.user.id
        fetchMedarbetare(session.user.id)
      } else {
        setLoading(false)
      }
    })

    // Lyssna på auth-ändringar
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Ignorera helt under tiden admin skapar en ny medarbetare (se authGuard.js)
      if (arBlockerad()) return

      if (event === 'SIGNED_OUT') {
        senasteEfterfragadeId.current = null
        setUser(null)
        setMedarbetare(null)
        setLoading(false)
      } else if (session?.user) {
        senasteEfterfragadeId.current = session.user.id
        setUser(session.user)
        fetchMedarbetare(session.user.id)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchMedarbetare(userId, forsok = 0) {
    const { data, error } = await supabase
      .from('medarbetare')
      .select('*')
      .eq('id', userId)
      .single()

    // Om en annan (senare) inloggning redan påbörjats under tiden vi väntade
    // på svar – kasta bort det här svaret, det gäller inte längre rätt person.
    if (senasteEfterfragadeId.current !== userId) return

    // PGRST116 = PostgREST-koden för "inga rader hittades" – det är EXAKT och
    // ENDAST det felet som betyder att kontot verkligen saknas (t.ex. raderat).
    // Alla andra fel (nätverksstrul, tillfällig överbelastning, etc.) ska ALDRIG
    // logga ut användaren – då lämnar vi bara läget som det är och försöker igen.
    const radenFinnsInteAlls = error?.code === 'PGRST116'

    if (!data && radenFinnsInteAlls) {
      // Kan bero på att kontot precis skapats och medarbetarraden inte hunnit
      // sparas än (kort race vid "Lägg till medarbetare") – försök igen ett par
      // gånger innan vi drar slutsatsen att kontot verkligen saknas.
      if (forsok < 3) {
        setTimeout(() => fetchMedarbetare(userId, forsok + 1), 700)
        return
      }
      // Kontot finns inte längre (t.ex. raderat) men en gammal session ligger kvar
      // i webbläsaren – logga ut istället för att fastna i en evig snurra.
      await supabase.auth.signOut()
      setUser(null)
      setMedarbetare(null)
      setLoading(false)
      return
    }

    if (error) {
      // Något annat fel (t.ex. tillfälligt nätverksstrul) – logga det, men rör
      // aldrig inloggningen. Behåll senast kända data istället för att nollställa.
      console.error('Kunde inte hämta medarbetardata:', error)
      setLoading(false)
      return
    }

    setMedarbetare(data)
    setLoading(false)

    // Kontot är avslutat (anonymiserat via "Avsluta anställning") – neka inloggning
    // även om de fortfarande har rätt lösenord kvar i Supabase Authentication.
    if (data.ar_aktiv === false) {
      alert('Det här kontot är inaktiverat. Kontakta admin om du tror att det här är fel.')
      await supabase.auth.signOut()
      setUser(null)
      setMedarbetare(null)
    }
  }

  async function refreshMedarbetare() {
    if (user) {
      senasteEfterfragadeId.current = user.id
      await fetchMedarbetare(user.id)
    }
  }

  async function logIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  async function logOut() {
    await supabase.auth.signOut()
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/nytt-losenord`
    })
    if (error) throw error
  }

  async function updatePassword(newPassword) {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
  }

  return (
    <AuthContext.Provider value={{
      user,
      medarbetare,
      loading,
      isAdmin: medarbetare?.ar_admin ?? false,
      logIn,
      logOut,
      resetPassword,
      updatePassword,
      refreshMedarbetare
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
